// src/components/ScrollProgress.tsx
// A persistent reading-progress line on the right edge.
// macOS auto-hides native scrollbars, so ::-webkit-scrollbar styling is
// invisible most of the time. This works in every browser, always visible.
//
// AppLayout sets `body { overflow: hidden }` and does the actual scrolling
// inside its own <main class="custom-scrollbar"> container, so window never
// scrolls — window.scrollY stays 0 everywhere in the app. This must track
// that real container (passed down as a ref from AppLayout), not window.
import { useEffect, useState, type RefObject } from 'react';

interface ScrollProgressProps {
  containerRef?: RefObject<HTMLElement | null>;
}

export const ScrollProgress = ({ containerRef }: ScrollProgressProps = {}) => {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = containerRef?.current;
    const getMetrics = () => el
      ? { scrollTop: el.scrollTop, scrollable: el.scrollHeight - el.clientHeight }
      : { scrollTop: window.scrollY, scrollable: document.documentElement.scrollHeight - window.innerHeight };

    const onScroll = () => {
      const { scrollTop, scrollable } = getMetrics();
      if (scrollable <= 0) { setVisible(false); return; }   // page doesn't scroll — don't imply it does
      setVisible(true);
      setProgress((scrollTop / scrollable) * 100);
    };
    onScroll();

    const target: EventTarget = el ?? window;
    target.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    // el's scrollHeight changes on route changes (same container, new page
    // content) without firing a scroll or window-resize event on its own.
    const resizeObserver = el ? new ResizeObserver(onScroll) : null;
    resizeObserver?.observe(el!);

    return () => {
      target.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      resizeObserver?.disconnect();
    };
  }, [containerRef]);

  if (!visible) return null;

  return (
    <div className="!fixed right-0 top-0 bottom-0 w-[3px] z-50 pointer-events-none bg-white/5">
      <div
        className="w-full bg-gradient-to-b from-blue-400 to-indigo-500 transition-[height] duration-75 ease-out"
        style={{ height: `${progress}%` }}
      />
    </div>
  );
};

export default ScrollProgress;
